import { X } from 'lucide-react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { UpdateForm } from '../types';
import { Checkbox } from './shadcn/checkbox';
import { Button } from './shadcn/button';
import { Input } from './shadcn/input';
import { Textarea } from './shadcn/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './shadcn/accordion';

export const DynamicSystemPromptRepeater = () => {
  const { control } = useFormContext<UpdateForm>();

  const {
    fields: dynamicPromptFields,
    insert: insertDynamicPrompt,
    remove: removeDynamicPrompt,
  } = useFieldArray({
    control,
    keyName: 'fieldId',
    name: 'dynamicSystemPrompts',
  });

  const {
    fields: promptVariableFields,
    insert: insertPromptVariable,
    remove: removePromptVariable,
  } = useFieldArray({
    control,
    keyName: 'fieldId',
    name: 'promptVariables',
  });

  return (
    <div className="p-4 flex flex-col space-y-6">
      <h2 className="text-xl font-semibold">Dynamic Prompts</h2>
      <div className="flex flex-row items-center">
        <p className="flex-grow">
          Dynamic prompts are system prompts that vary based on the conversation metadata.
        </p>
        <Button
          variant="outline"
          className="text-green-600 border-green-600"
          onClick={() =>
            insertDynamicPrompt(dynamicPromptFields.length, {
              prompt: '',
              name: '',
              condition: '',
              zIndex: 0,
            })
          }
        >
          ＋ Add
        </Button>
      </div>
      <div className="flex flex-col space-y-4 items-center justify-center">
        <div className="flex-grow w-full">
          {dynamicPromptFields?.map((field, index) => (
            <Accordion type="single" collapsible key={field.fieldId}>
              <AccordionItem value={`item-${index}`}>
                <div className="flex items-center">
                  <AccordionTrigger>
                    {field.name || `Dynamic System Prompt ${index + 1}`}
                  </AccordionTrigger>
                  <div className="flex-grow" />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDynamicPrompt(index);
                    }}
                    className="mr-4"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <AccordionContent>
                  <div className="flex flex-col space-y-4">
                    <div className="flex flex-row space-x-4">
                      <Controller
                        name={`dynamicSystemPrompts.${index}.name`}
                        render={({ field }) => (
                          <div className="flex-grow">
                            <label
                              htmlFor={`dynamicSystemPrompts[${index}].name`}
                              className="block text-sm font-medium mb-1"
                            >
                              Name
                            </label>
                            <Input
                              {...field}
                              id={`dynamicSystemPrompts[${index}].name`}
                              type="text"
                              name={`dynamicSystemPrompts[${index}].name`}
                              defaultValue={field.value}
                            />
                          </div>
                        )}
                        control={control}
                        defaultValue={field.name}
                      />
                      <Controller
                        name={`dynamicSystemPrompts.${index}.condition`}
                        render={({ field }) => (
                          <div className="flex-grow">
                            <label
                              htmlFor={`dynamicSystemPrompts[${index}].condition`}
                              className="block text-sm font-medium mb-1"
                            >
                              Condition
                            </label>
                            <Input
                              {...field}
                              id={`dynamicSystemPrompts[${index}].condition`}
                              type="text"
                              name={`dynamicSystemPrompts[${index}].condition`}
                              defaultValue={field.value}
                            />
                          </div>
                        )}
                        control={control}
                        defaultValue={field.condition}
                      />
                      <Controller
                        render={({ field }) => (
                          <div>
                            <label
                              htmlFor={`dynamicSystemPrompts[${index}].zIndex`}
                              className="block text-sm font-medium mb-1"
                            >
                              Z-Index
                            </label>
                            <Input
                              {...field}
                              id={`dynamicSystemPrompts[${index}].zIndex`}
                              type="number"
                              name={`dynamicSystemPrompts[${index}].zIndex`}
                              defaultValue={field.value}
                              className="w-24"
                            />
                          </div>
                        )}
                        name={`dynamicSystemPrompts.${index}.zIndex`}
                        control={control}
                        defaultValue={field.zIndex}
                      />
                    </div>
                    <Controller
                      render={({ field }) => (
                        <div>
                          <label
                            htmlFor={`dynamicSystemPrompts[${index}].prompt`}
                            className="block text-sm font-medium mb-1"
                          >
                            Prompt
                          </label>
                          <Textarea
                            {...field}
                            id={`dynamicSystemPrompts[${index}].prompt`}
                            name={`dynamicSystemPrompts[${index}].prompt`}
                            defaultValue={field.value}
                            rows={20}
                            className="w-full"
                          />
                        </div>
                      )}
                      name={`dynamicSystemPrompts.${index}.prompt`}
                      control={control}
                      defaultValue={field.prompt}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ))}
        </div>
      </div>
      <h2 className="text-xl font-semibold">Prompt Variables</h2>
      <div className="flex flex-row items-center">
        <p className="flex-grow">
          Prompt variables are placeholders that can be injected into prompts.
        </p>
        <Button
          variant="outline"
          className="text-green-600 border-green-600"
          onClick={() =>
            insertPromptVariable(promptVariableFields.length, {
              name: '',
              value: '',
              injectToStartOfSystemPrompt: false,
              injectToEndOfSystemPrompt: false,
              injectToStartOfDynamicPrompt: false,
              injectToEndOfDynamicPrompt: false,
            })
          }
        >
          ＋ Add Variable
        </Button>
      </div>
      <div className="flex flex-col space-y-4 items-center justify-center">
        <div className="flex-grow w-full">
          {promptVariableFields?.map((field, index) => (
            <Accordion type="single" collapsible key={field.fieldId}>
              <AccordionItem value={`item-${index}`}>
                <div className="flex items-center">
                  <AccordionTrigger>
                    {field.name || `Prompt Variable ${index + 1}`}
                  </AccordionTrigger>
                  <div className="flex-grow" />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      removePromptVariable(index);
                    }}
                    className="mr-4"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <AccordionContent>
                  <div className="flex flex-col space-y-4">
                    <Controller
                      name={`promptVariables.${index}.name`}
                      render={({ field }) => (
                        <div>
                          <label
                            htmlFor={`promptVariables[${index}].name`}
                            className="block text-sm font-medium mb-1"
                          >
                            Name
                          </label>
                          <Input
                            {...field}
                            id={`promptVariables[${index}].name`}
                            type="text"
                            name={`promptVariables[${index}].name`}
                            defaultValue={field.value}
                            className="w-full"
                          />
                        </div>
                      )}
                      control={control}
                      defaultValue={field.name}
                    />
                    <Controller
                      name={`promptVariables.${index}.value`}
                      render={({ field }) => (
                        <div>
                          <label
                            htmlFor={`promptVariables[${index}].value`}
                            className="block text-sm font-medium mb-1"
                          >
                            Value
                          </label>
                          <Textarea
                            {...field}
                            id={`promptVariables[${index}].value`}
                            name={`promptVariables[${index}].value`}
                            defaultValue={field.value}
                            rows={4}
                            className="w-full"
                          />
                        </div>
                      )}
                      control={control}
                      defaultValue={field.value}
                    />
                    <div className="flex flex-row space-x-4">
                      <Controller
                        name={`promptVariables.${index}.injectToStartOfSystemPrompt`}
                        render={({ field }) => (
                          <div className="flex flex-row items-center gap-2 flex-grow">
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onCheckedChange={(value) => field.onChange(!!value.valueOf())}
                              value={undefined}
                              id={`promptVariables[${index}].injectToStartOfSystemPrompt`}
                              name={`promptVariables[${index}].injectToStartOfSystemPrompt`}
                            />
                            <label
                              htmlFor={`promptVariables[${index}].injectToStartOfSystemPrompt`}
                              className="text-sm"
                            >
                              Inject to Start of System Prompt
                            </label>
                          </div>
                        )}
                        control={control}
                        defaultValue={field.injectToStartOfSystemPrompt}
                      />
                      <Controller
                        name={`promptVariables.${index}.injectToEndOfSystemPrompt`}
                        render={({ field }) => (
                          <div className="flex flex-row items-center gap-2 flex-grow">
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onCheckedChange={(value) => field.onChange(!!value.valueOf())}
                              value={undefined}
                              id={`promptVariables[${index}].injectToEndOfSystemPrompt`}
                              name={`promptVariables[${index}].injectToEndOfSystemPrompt`}
                            />
                            <label
                              htmlFor={`promptVariables[${index}].injectToEndOfSystemPrompt`}
                              className="text-sm"
                            >
                              Inject to End of System Prompt
                            </label>
                          </div>
                        )}
                        control={control}
                        defaultValue={field.injectToEndOfSystemPrompt}
                      />
                      <div className="flex-grow" />
                    </div>
                    <div className="flex flex-row space-x-4">
                      <Controller
                        name={`promptVariables.${index}.injectToStartOfDynamicPrompt`}
                        render={({ field }) => (
                          <div className="flex flex-row items-center gap-2 flex-grow">
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onCheckedChange={(value) => field.onChange(!!value.valueOf())}
                              value={undefined}
                              id={`promptVariables[${index}].injectToStartOfDynamicPrompt`}
                              name={`promptVariables[${index}].injectToStartOfDynamicPrompt`}
                            />
                            <label
                              htmlFor={`promptVariables[${index}].injectToStartOfDynamicPrompt`}
                              className="text-sm"
                            >
                              Inject to Start of Dynamic Prompt
                            </label>
                          </div>
                        )}
                        control={control}
                        defaultValue={field.injectToStartOfDynamicPrompt}
                      />
                      <Controller
                        name={`promptVariables.${index}.injectToEndOfDynamicPrompt`}
                        render={({ field }) => (
                          <div className="flex flex-row items-center gap-2 flex-grow">
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onCheckedChange={(value) => field.onChange(!!value.valueOf())}
                              value={undefined}
                              id={`promptVariables[${index}].injectToEndOfDynamicPrompt`}
                              name={`promptVariables[${index}].injectToEndOfDynamicPrompt`}
                            />
                            <label
                              htmlFor={`promptVariables[${index}].injectToEndOfDynamicPrompt`}
                              className="text-sm"
                            >
                              Inject to End of Dynamic Prompt
                            </label>
                          </div>
                        )}
                        control={control}
                        defaultValue={field.injectToEndOfDynamicPrompt}
                      />
                      <div className="flex-grow" />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ))}
        </div>
      </div>
    </div>
  );
};
